import "./design-review.css";
import { CloudflareWebAuth } from "./auth";
import { PRODUCT, WEB_ROUTES } from "./config";
import {
  CloudflareMarkAttentionRepository,
  MarkAttentionRepositoryError,
} from "./mark-attention";

interface DesignReviewAuth {
  session(): Promise<{ authenticated: boolean }>;
}

interface OwnerCapabilityProbe {
  load(): Promise<unknown>;
}

const GITHUB = "https://github.com/markitics/agenda/issues";
const APPLE = {
  guidelines: "https://developer.apple.com/design/human-interface-guidelines/",
  principles:
    "https://developer.apple.com/design/human-interface-guidelines/design-principles",
  ios:
    "https://developer.apple.com/design/human-interface-guidelines/designing-for-ios",
  accessibility:
    "https://developer.apple.com/design/human-interface-guidelines/accessibility",
  feedback:
    "https://developer.apple.com/design/human-interface-guidelines/feedback",
  motion:
    "https://developer.apple.com/design/human-interface-guidelines/motion",
  awards: "https://developer.apple.com/design/awards/",
} as const;

function issue(number: number): string {
  return `${GITHUB}/${number}`;
}

function setMetaDescription(content: string): void {
  let meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "description";
    document.head.append(meta);
  }
  meta.content = content;
}

function setCanonical(href: string): void {
  let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "canonical";
    document.head.append(link);
  }
  link.href = href;
}

function gate(eyebrow: string, title: string, copy: string): HTMLElement {
  const main = document.createElement("main");
  main.className = "gate-shell";
  main.id = "app-content";
  main.tabIndex = -1;
  const card = document.createElement("section");
  card.className = "gate-card";
  const mark = document.createElement("span");
  mark.className = "brand-mark";
  mark.setAttribute("aria-hidden", "true");
  mark.textContent = PRODUCT.brandMark;
  const eyebrowElement = document.createElement("p");
  eyebrowElement.className = "eyebrow";
  eyebrowElement.textContent = eyebrow;
  const heading = document.createElement("h1");
  heading.textContent = title;
  const description = document.createElement("p");
  description.className = "gate-copy";
  description.textContent = copy;
  card.append(mark, eyebrowElement, heading, description);
  main.append(card);
  return main;
}

function appendGateLink(main: HTMLElement, label: string): void {
  const link = document.createElement("a");
  link.className = "primary-link gate-primary-action";
  link.href = WEB_ROUTES.app;
  link.textContent = label;
  main.querySelector(".gate-card")?.append(link);
}

export class DesignReviewPage {
  private readonly auth: DesignReviewAuth;
  private readonly ownerProbe: OwnerCapabilityProbe;

  constructor(
    private readonly root: HTMLElement,
    dependencies: {
      auth?: DesignReviewAuth;
      ownerProbe?: OwnerCapabilityProbe;
    } = {},
  ) {
    this.auth = dependencies.auth ?? new CloudflareWebAuth();
    this.ownerProbe = dependencies.ownerProbe
      ?? new CloudflareMarkAttentionRepository();
  }

  async start(): Promise<void> {
    document.documentElement.dataset.surface = "design-review";
    document.title = `Design review · ${PRODUCT.name}`;
    setMetaDescription(
      "Convoful's owner-only, source-backed design review against Apple platform guidance.",
    );
    setCanonical(`${WEB_ROUTES.canonicalOrigin}${WEB_ROUTES.designReview}`);
    const skipLink = document.querySelector<HTMLAnchorElement>(".skip-link");
    if (skipLink) skipLink.href = "#design-review-main";
    this.root.replaceChildren(gate(
      "Owner-only design review",
      "Opening the design review…",
      "Convoful is verifying this browser before showing the report.",
    ));

    try {
      const session = await this.auth.session();
      if (!session.authenticated) {
        const signedOut = gate(
          "Owner-only",
          "Design review stays private.",
          "Sign in to Convoful, then return to /for-mark/design. No design report was shown.",
        );
        appendGateLink(signedOut, "Sign in");
        this.root.replaceChildren(signedOut);
        return;
      }
      await this.ownerProbe.load();
      this.renderReview();
    } catch (error) {
      if (
        error instanceof MarkAttentionRepositoryError
        && error.status === 403
      ) {
        const ownerOnly = gate(
          "Owner-only",
          "This design review belongs to Mark.",
          "This signed-in account does not have the owner capability. No design report is shown.",
        );
        appendGateLink(ownerOnly, "Return to Convoful");
        this.root.replaceChildren(ownerOnly);
        return;
      }
      const unavailable = gate(
        "Private design review",
        "The design review stayed closed.",
        "Convoful could not verify an authenticated owner response, so the report was not shown.",
      );
      appendGateLink(unavailable, "Return to Convoful");
      this.root.replaceChildren(unavailable);
    }
  }

  private renderReview(): void {
    this.root.innerHTML = `
      <div class="design-review-shell">
        <header class="design-review-header">
          <a class="design-review-brand" href="${WEB_ROUTES.markAttention}" aria-label="For Mark home">
            <span class="brand-mark brand-mark-small" aria-hidden="true">${PRODUCT.brandMark}</span>
            <span>For Mark</span>
          </a>
          <nav aria-label="Design review">
            <a href="#priorities">Priorities</a>
            <a href="#apple-bar">Apple bar</a>
            <a href="#award-lens">Award lens</a>
          </nav>
        </header>

        <main id="design-review-main" tabindex="-1">
          <section class="design-review-hero" aria-labelledby="design-review-title">
            <div>
              <p class="eyebrow">Live design review · 3 August 2026</p>
              <h1 id="design-review-title">Make the card feel inevitable.</h1>
              <p class="design-review-lede">
                Convoful already has a memorable visual world. The fastest path to
                award-grade design is subtraction: one private card, one obvious action,
                and immediate tactile truth.
              </p>
            </div>
            <aside class="design-review-verdict" aria-label="Candid design verdict">
              <span>Brutal read</span>
              <strong>Distinctive enough to remember. Not yet quiet or dependable enough to disappear.</strong>
              <dl>
                <div><dt>Interaction</dt><dd>P1 in review</dd></div>
                <div><dt>Clutter</dt><dd>P1 owned</dd></div>
                <div><dt>Inclusive use</dt><dd>Acceptance open</dd></div>
              </dl>
            </aside>
          </section>

          <section class="design-review-section" id="priorities" aria-labelledby="priorities-title">
            <header class="design-review-section-heading">
              <div>
                <p class="eyebrow">Do next</p>
                <h2 id="priorities-title">Three things matter more than more features.</h2>
              </div>
              <p>Each finding already has one canonical GitHub owner.</p>
            </header>
            <div class="design-finding-grid">
              <article class="design-finding design-finding-critical">
                <span class="design-status">P1 · source merged</span>
                <p class="design-number" aria-hidden="true">01</p>
                <h3>Never let a touch feel dead.</h3>
                <p>Build 32 failed the real two-activation Reveal ritual with Reduce Motion. The source correction is merged; a tester-visible carrier and direct physical reacceptance still matter.</p>
                <div class="design-links">
                  <a href="${issue(400)}" target="_blank" rel="noreferrer">Interaction issue #400 <span aria-hidden="true">↗</span></a>
                  <a href="${APPLE.feedback}" target="_blank" rel="noreferrer">Apple: Feedback <span aria-hidden="true">↗</span></a>
                </div>
              </article>
              <article class="design-finding design-finding-high">
                <span class="design-status">P1 · owned</span>
                <p class="design-number" aria-hidden="true">02</p>
                <h3>Say a failure once.</h3>
                <p>One Undo failure is explained three times: “Undo wasn’t confirmed,” “Conversation refresh required,” then “Card marked discussed · Undo.” At XXXL, warning/status, filters/count, and the continuity-code pill push the card below the initial viewport. Keep one recovery surface with one action; make the card the next obvious content.</p>
                <div class="design-links">
                  <a href="${issue(372)}" target="_blank" rel="noreferrer">Deck clutter #372 <span aria-hidden="true">↗</span></a>
                  <a href="${APPLE.feedback}" target="_blank" rel="noreferrer">Apple: Feedback <span aria-hidden="true">↗</span></a>
                </div>
              </article>
              <article class="design-finding">
                <span class="design-status">P1 · acceptance open</span>
                <p class="design-number" aria-hidden="true">03</p>
                <h3>Make accessibility part of the ritual.</h3>
                <p>Keep the satisfying swipe, but never make it the only path. Dynamic Type, VoiceOver, keyboard access, Reduce Motion, and a visible tap alternative should feel designed—not accommodated.</p>
                <div class="design-links">
                  <a href="${issue(64)}" target="_blank" rel="noreferrer">Accessibility #64 <span aria-hidden="true">↗</span></a>
                  <a href="${APPLE.accessibility}" target="_blank" rel="noreferrer">Apple: Accessibility <span aria-hidden="true">↗</span></a>
                </div>
              </article>
            </div>
          </section>

          <section class="design-review-section" id="apple-bar" aria-labelledby="apple-bar-title">
            <header class="design-review-section-heading">
              <div>
                <p class="eyebrow">Apple bar</p>
                <h2 id="apple-bar-title">Four rules for every Convoful screen.</h2>
              </div>
              <a class="design-section-link" href="${APPLE.guidelines}" target="_blank" rel="noreferrer">Human Interface Guidelines <span aria-hidden="true">↗</span></a>
            </header>
            <ol class="design-rule-grid">
              <li><span>01</span><strong>The card is the protagonist.</strong><p>Controls support content. They do not compete with it.</p></li>
              <li><span>02</span><strong>One layer of feedback.</strong><p>Put status beside what changed; interrupt only for consequential action.</p></li>
              <li><span>03</span><strong>Motion explains cause.</strong><p>Follow the finger, keep physics coherent, and preserve meaning when motion is reduced.</p></li>
              <li><span>04</span><strong>Privacy is structural.</strong><p>Face-down means absent—not blurred, hidden in color, or explained away.</p></li>
            </ol>
            <div class="design-apple-links" aria-label="Apple design references">
              <a href="${APPLE.principles}" target="_blank" rel="noreferrer">Design principles</a>
              <a href="${APPLE.ios}" target="_blank" rel="noreferrer">Designing for iOS</a>
              <a href="${APPLE.motion}" target="_blank" rel="noreferrer">Motion</a>
              <a href="${APPLE.accessibility}" target="_blank" rel="noreferrer">Accessibility</a>
            </div>
          </section>

          <section class="design-review-section" id="award-lens" aria-labelledby="award-lens-title">
            <header class="design-review-section-heading">
              <div>
                <p class="eyebrow">Future Apple Design Award lens</p>
                <h2 id="award-lens-title">The concept is ahead of the finish.</h2>
              </div>
              <a class="design-section-link" href="${APPLE.awards}" target="_blank" rel="noreferrer">Apple Design Awards <span aria-hidden="true">↗</span></a>
            </header>
            <div class="award-lens-grid">
              <article data-state="promising"><span>Promising</span><h3>Delight & Fun</h3><p>The card ritual and color world have charm. Recovery chrome currently breaks the spell.</p></article>
              <article data-state="open"><span>Not proven</span><h3>Inclusivity</h3><p>Strong test intent; full real-use Dynamic Type and VoiceOver acceptance remains open.</p></article>
              <article data-state="strong"><span>Strong concept</span><h3>Innovation</h3><p>Saving a thought privately, then revealing it together, is a crisp product idea.</p></article>
              <article data-state="risk"><span>At risk</span><h3>Interaction</h3><p>A tactile app cannot tolerate a dead Reveal or ambiguous tap-versus-drag response.</p></article>
              <article data-state="promising"><span>Needs evidence</span><h3>Social Impact</h3><p>The mission is human connection. Repeated real playtests must prove that outcome.</p></article>
              <article data-state="promising"><span>Distinctive</span><h3>Visuals & Graphics</h3><p>The palette is ownable. Photo containment and error states still need the same finish.</p></article>
            </div>
          </section>

          <section class="design-review-section design-review-progress" aria-labelledby="progress-title">
            <header class="design-review-section-heading">
              <div>
                <p class="eyebrow">Past feedback carried forward</p>
                <h2 id="progress-title">Do not rediscover solved problems.</h2>
              </div>
            </header>
            <ul>
              <li><span>Closed</span><strong>Homepage headline now breathes.</strong><a href="${issue(223)}" target="_blank" rel="noreferrer">#223</a></li>
              <li><span>Shipped direction</span><strong>The tactile, joyful card world remains the right foundation.</strong><a href="${issue(7)}" target="_blank" rel="noreferrer">#7</a></li>
              <li><span>Partly delivered</span><strong>The Deck masthead is gone and active audience is static; recovery clutter remains.</strong><a href="${issue(372)}" target="_blank" rel="noreferrer">#372</a></li>
              <li><span>Open finish</span><strong>Personal photo backs must stay fully inside their card frame.</strong><a href="${issue(93)}" target="_blank" rel="noreferrer">#93</a></li>
            </ul>
          </section>

          <footer class="design-review-footer">
            <p><strong>Evidence snapshot:</strong> refreshed 3 August 2026 from current GitHub, current-main source, live Web renders, and privacy-safe iOS test evidence. GitHub remains the project source of truth.</p>
            <p>This is an internal quality bar, not an Apple nomination, endorsement, or award claim.</p>
          </footer>
        </main>
      </div>`;

    requestAnimationFrame(() => {
      document.querySelector<HTMLElement>("#design-review-main")?.focus({
        preventScroll: true,
      });
    });
  }
}

export async function renderDesignReview(root: HTMLElement): Promise<void> {
  await new DesignReviewPage(root).start();
}
